import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import {
  incomingItemCategoryGroups,
  incomingItemCreateSchema,
  standardRouteTypes,
  type IncomingItem,
  type IncomingItemCreateInput,
} from './types';
import {
  useCreateIncomingItem,
  useProposeIncomingItemChange,
  useUpdateIncomingItemDirect,
} from './useIncomingItems';
import { IncomingItemAliasesPanel } from './IncomingItemAliasesPanel';

/** True for a route an operator may still assign. */
const isAssignableRoute = (
  route: string | null | undefined,
): route is (typeof standardRouteTypes)[number] =>
  !!route && (standardRouteTypes as readonly string[]).includes(route);

const ROUTE_NONE = '__none__';

export type IncomingItemsDialogMode = 'direct' | 'proposal';

interface IncomingItemsFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied the dialog runs in edit mode. */
  row?: IncomingItem | null;
  /** `direct` → PUT, `proposal` → POST /propose-change. Ignored on create. */
  mode?: IncomingItemsDialogMode;
}

export const IncomingItemsFormDialog = ({
  open,
  onOpenChange,
  row,
  mode = 'direct',
}: IncomingItemsFormDialogProps) => {
  const { t } = useTranslation();
  const create = useCreateIncomingItem();
  const updateDirect = useUpdateIncomingItemDirect();
  const propose = useProposeIncomingItemChange();
  const isEdit = !!row;
  const isProposal = isEdit && mode === 'proposal';
  // A historical route that can be read but not re-selected: secondary_process,
  // and the light/heavy dismantling split retired in migration 0032.
  const legacyDefaultRoute =
    row?.default_route_type && !isAssignableRoute(row.default_route_type)
      ? row.default_route_type
      : null;

  // Reason is required only in proposal mode. Compose the schema dynamically
  // so the resolver still validates the row fields the same way.
  const formSchema = isProposal
    ? incomingItemCreateSchema.extend({
        reason: z.string().min(1, t('efs.masters.form.reason_required', { ns: 'masters' })),
      })
    : incomingItemCreateSchema.extend({ reason: z.string().optional() });

  type FormValues = IncomingItemCreateInput & { reason?: string };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      japanese_name: '',
      normalized_name: '',
      category_group: 'other',
      effective_date: new Date().toISOString().slice(0, 10),
      default_route_type: null,
      reason: '',
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset({
        japanese_name: '',
        normalized_name: '',
        category_group: 'other',
        effective_date: new Date().toISOString().slice(0, 10),
        default_route_type: null,
        reason: '',
      });
      return;
    }
    if (row) {
      form.reset({
        japanese_name: row.japanese_name,
        normalized_name: row.normalized_name,
        category_group: row.category_group,
        effective_date: row.effective_date,
        // Retired routes remain readable, but omitting the field preserves the
        // historical value unless the operator explicitly chooses a new route.
        // Retired routes are anything outside the current set: the light/heavy
        // dismantling split as well as secondary_process.
        default_route_type: isAssignableRoute(row.default_route_type)
          ? row.default_route_type
          : row.default_route_type
            ? undefined
            : null,
        reason: '',
      });
    }
  }, [open, row, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const { reason, ...payload } = values;
    if (isEdit && row) {
      if (isProposal) {
        await propose.mutateAsync({
          id: row.incoming_item_id,
          body: { proposed: payload, reason: reason ?? '' },
        });
      } else {
        await updateDirect.mutateAsync({ id: row.incoming_item_id, body: payload });
      }
    } else {
      await create.mutateAsync(payload);
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || updateDirect.isPending || propose.isPending;

  const title = !isEdit
    ? t('efs.masters.incoming.create', { ns: 'masters' })
    : isProposal
      ? t('efs.masters.form.mode_proposal', { ns: 'masters' })
      : t('efs.masters.form.mode_direct', { ns: 'masters' });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {t('efs.masters.incoming.create_subtitle', {
              ns: 'masters',
              defaultValue: 'マスター登録は承認後に有効化されます',
            })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="japanese_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.masters.fields.japanese_name', { ns: 'masters' })}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="normalized_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.fields.normalized_name', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="category_group"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.masters.fields.category_group', { ns: 'masters' })}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {incomingItemCategoryGroups.map((group) => (
                          <SelectItem key={group} value={group}>
                            {t(`efs.masters.category_group.${group}`, {
                              ns: 'masters',
                              defaultValue: group,
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="effective_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.masters.fields.effective_date', { ns: 'masters' })}</FormLabel>
                  <FormControl>
                    <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Standard sorting route — used to pre-fill the Sorting page when
                a package of this item is classified. Operators can override
                per-package at sort time. */}
            <FormField
              control={form.control}
              name="default_route_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.fields.default_route_type', {
                      ns: 'masters',
                      defaultValue: '標準處理方式',
                    })}
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={field.value ?? ROUTE_NONE}
                      onValueChange={(v) => field.onChange(v === ROUTE_NONE ? null : v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ROUTE_NONE}>
                          {t('efs.masters.route_type.none', {
                            ns: 'masters',
                            defaultValue: '（未設定 — 分類時手動指定）',
                          })}
                        </SelectItem>
                        {standardRouteTypes.map((rt) => (
                          <SelectItem key={rt} value={rt}>
                            {t(`efs.masters.route_type.${rt}`, {
                              ns: 'masters',
                              defaultValue: rt,
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {legacyDefaultRoute ? (
                    <FormDescription data-testid="legacy-default-route">
                      {t(`efs.masters.route_type.${legacyDefaultRoute}`, {
                        ns: 'masters',
                        defaultValue: legacyDefaultRoute,
                      })}{' '}
                      {t('efs.masters.route_type.legacy_read_only', {
                        ns: 'masters',
                        defaultValue: '（履歴設定・再選択不可）',
                      })}
                    </FormDescription>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            {isEdit && row ? (
              <IncomingItemAliasesPanel
                incomingItemId={row.incoming_item_id}
                normalizedName={row.normalized_name}
              />
            ) : null}

            {isProposal ? (
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.masters.form.reason', { ns: 'masters' })}</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={3}
                        placeholder={t('efs.masters.form.reason_placeholder', {
                          ns: 'masters',
                        })}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save')
                  : t('efs.masters.actions.create', { ns: 'masters' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
